import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HavingTroubleLink } from "./HavingTroubleLink";

export function MeetingDisplayNameGate({
  productName,
  imageUrl,
  pageTitle,
  defaultDisplayName = "",
  description,
  submitLabel,
  submittingLabel = "Joining…",
  submitting,
  error,
  onSubmit,
  onOpenChat,
}: {
  productName: string;
  imageUrl?: string;
  pageTitle: string;
  defaultDisplayName?: string;
  description: string;
  submitLabel: string;
  submittingLabel?: string;
  submitting: boolean;
  error: string | null;
  onSubmit: ({ displayName }: { displayName: string }) => void;
  onOpenChat?: () => void;
}) {
  const [displayName, setDisplayName] = useState(defaultDisplayName);

  useEffect(() => {
    if (defaultDisplayName) {
      setDisplayName(defaultDisplayName);
    }
  }, [defaultDisplayName]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = displayName.trim();
    if (!trimmed || submitting) return;
    onSubmit({ displayName: trimmed });
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <div className="min-h-dvh flex flex-col bg-black text-white">
        <div className="flex flex-1 flex-col items-center justify-center px-6">
          <div className="w-full max-w-sm space-y-6">
            <div className="text-center">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={productName}
                  className="mx-auto mb-4 h-40 w-40 rounded-lg object-cover shadow-xl"
                />
              ) : null}
              <h1 className="text-2xl font-bold">{productName}</h1>
              <p className="mt-2 text-sm text-white/60">{description}</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="meeting-display-name" className="mb-1 block text-sm text-white">
                  Display name
                </label>
                <Input
                  id="meeting-display-name"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="Display name"
                  autoComplete="nickname"
                  autoFocus
                  disabled={submitting}
                  className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
                />
              </div>
              {error ? <p className="text-sm text-red-400">{error}</p> : null}
              <Button type="submit" disabled={submitting || !displayName.trim()} className="w-full">
                {submitting ? submittingLabel : submitLabel}
              </Button>
            </form>
          </div>
        </div>
        {onOpenChat ? (
          <div className="shrink-0 px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            <HavingTroubleLink onOpenChat={onOpenChat} />
          </div>
        ) : null}
      </div>
    </>
  );
}
